import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faSpinner, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import './NoteDrafts.scss';
import { AutoSaveStatus } from '../../../note-drafts/useAutoSaveNoteDraft';

interface AutoSaveIndicatorProps {
  status: AutoSaveStatus;
  lastSavedAt: Date | null;
  errorMessage?: string;
  onRetry?: () => void;
}

const formatTimeAgo = (date: Date): string => {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);

  if (seconds < 5) {
    return 'just now';
  }
  if (seconds < 60) {
    return `${seconds}s ago`;
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
};

export const AutoSaveIndicator = ({ status, lastSavedAt, errorMessage, onRetry }: AutoSaveIndicatorProps) => {
  if (status === 'idle' && !lastSavedAt) {
    return null;
  }

  return (
    <div className='autosave-indicator'>
      {status === 'saving' && (
        <span className='autosave-saving'>
          <FontAwesomeIcon icon={faSpinner} spin className='m-r-4' />
          Saving...
        </span>
      )}
      {status === 'saved' && lastSavedAt && (
        <span className='autosave-saved'>
          <FontAwesomeIcon icon={faCheck} className='m-r-4' />
          Saved as draft {formatTimeAgo(lastSavedAt)}
        </span>
      )}
      {status === 'error' && (
        <span className='autosave-error'>
          <FontAwesomeIcon icon={faTriangleExclamation} className='m-r-4' />
          {errorMessage || 'Failed to save'}
          {onRetry && (
            <span className='autosave-retry' onClick={onRetry}>
              Retry
            </span>
          )}
        </span>
      )}
      {status === 'idle' && lastSavedAt && (
        <span className='autosave-idle'>
          <FontAwesomeIcon icon={faCheck} className='m-r-4' />
          Saved {formatTimeAgo(lastSavedAt)}
        </span>
      )}
    </div>
  );
};
